import { Capacitor, registerPlugin } from "@capacitor/core";
import { savePasswordCredential } from "@/utils/passwordCredential";

/*
  登入頁「記住帳號密碼」（2026-10-21）。

  我們的網站自己不存密碼（網頁能存東西的地方都是明文，見 utils/passwordCredential.js），
  勾了以後交給「這台裝置上安全的地方」保存：
    - App：手機的 Keystore 加密保存（android/.../SecureCredentialsPlugin.java），
           下次打開登入頁直接帶入帳號密碼 —— 跟銀行、股票 App 的「記住密碼」一樣
    - 網頁：瀏覽器的密碼管理員（Chrome 會跳出「要儲存密碼嗎？」），下次由瀏覽器自動填入
  帳號（email）照舊記在 localStorage 的 _acc（帳號不是秘密）。

  沒勾：清掉 App 存的帳號密碼與 _acc。瀏覽器密碼管理員裡的由使用者自己在瀏覽器管理（網站刪不到）。

  ## 舊版存過的密碼（_pwd）

  2026-10-20 前的「記住帳號密碼」把密碼明文存在 localStorage 的 _pwd。不要讓這些人重新輸入，
  搬到安全的地方之後把明文刪掉：
    - 新版 App：開 App 就搬進手機 Keystore（migrateLegacySavedPassword，main.js 呼叫）
    - 網頁：瀏覽器的密碼管理員要使用者同意才存得進去，只能等他到登入頁 ——
      帶入密碼欄、立刻刪掉明文，按登入後瀏覽器會詢問要不要儲存
    - 還沒更新的舊版 App（沒有 Keystore 外掛）：先不動，等更新後開 App 時再搬，免得他們每次都要重新輸入

  ## 多個帳號（2026-10-30「切換帳號」）

  App versionCode 3 起，Keystore 外掛每個帳號各存一組，切換帳號時直接拿那組登入（utils/accountSwitch.js）。
  指定帳號的讀取、刪除帶 username；舊版外掛（versionCode 2）會忽略這個參數 ——
  讀取回傳唯一那組（可能是別的帳號），所以拿到的帳號一定要核對；刪除則是全部清掉（舊版本來就只有一組）。
*/
const SecureCredentials = registerPlugin("SecureCredentials");
const LEGACY_PASSWORD_KEY = "_pwd";

/** 這台裝置能不能由 App 加密保存（舊版 App 沒有這個外掛） */
export function hasSecureCredentialStore() {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("SecureCredentials");
}

/** 還沒更新的舊版 App：沒有安全的地方可以搬，舊的明文密碼先留著 */
function isOldAppWithoutStore() {
  return Capacitor.isNativePlatform() && !Capacitor.isPluginAvailable("SecureCredentials");
}

/** 新版 App 開啟時：舊版明文存的密碼搬進手機 Keystore，然後刪掉明文 */
export async function migrateLegacySavedPassword() {
  if (!hasSecureCredentialStore()) return;
  const password = localStorage.getItem(LEGACY_PASSWORD_KEY);
  if (!password) return;
  const email = localStorage.getItem("_acc");
  if (!email) {
    localStorage.removeItem(LEGACY_PASSWORD_KEY);
    return;
  }
  try {
    await SecureCredentials.save({ username: email, password });
    localStorage.removeItem(LEGACY_PASSWORD_KEY);
  } catch (e) {
    /* 存不進去就留著，下次開 App 再搬 */
  }
}

const sameEmail = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/** App 存的某個帳號的那組；沒存（或舊版外掛回的是別的帳號）回 null */
async function getSavedFor(email) {
  try {
    const saved = await SecureCredentials.get({ username: email });
    if (saved && saved.username && saved.password && sameEmail(saved.username, email)) return saved;
  } catch (e) {
    /* 讀不到就當沒存 */
  }
  return null;
}

/**
 * 登入頁打開時帶入：{ email, password }。
 * 給了 email（切換帳號）：只拿那個帳號的密碼，網頁一律沒有密碼（交給瀏覽器的密碼管理員）
 */
export async function loadSavedLogin(forEmail = null) {
  if (forEmail) {
    if (!hasSecureCredentialStore()) return { email: forEmail, password: null };
    const saved = await getSavedFor(forEmail);
    return { email: forEmail, password: saved ? saved.password : null };
  }

  const email = localStorage.getItem("_acc");

  if (hasSecureCredentialStore()) {
    await migrateLegacySavedPassword();
    try {
      const saved = await SecureCredentials.get();
      if (saved && saved.username && saved.password) return { email: saved.username, password: saved.password };
    } catch (e) {
      /* 讀不到就當沒存 */
    }
    return { email: email || null, password: null };
  }

  // 網頁（與舊版 App）：舊版明文存的密碼帶入一次
  const legacy = localStorage.getItem(LEGACY_PASSWORD_KEY);
  // 網頁帶入後立刻刪掉，按登入後交給瀏覽器的密碼管理員（rememberLogin）
  if (!isOldAppWithoutStore()) localStorage.removeItem(LEGACY_PASSWORD_KEY);
  return { email: email || null, password: legacy || null };
}

/** 登入成功後：勾了就保存，沒勾就清掉（只清這個帳號的） */
export async function rememberLogin(remember, email, password, name) {
  if (!remember) {
    localStorage.removeItem("_acc");
    localStorage.removeItem(LEGACY_PASSWORD_KEY);
    await forgetSavedLogin(email);
    return;
  }
  localStorage.setItem("_acc", email);
  if (hasSecureCredentialStore()) {
    localStorage.removeItem(LEGACY_PASSWORD_KEY);
    try {
      await SecureCredentials.save({ username: email, password });
    } catch (e) {
      /* 存不進去就算了，下次再輸入 */
    }
    return;
  }
  // 舊版 App 的明文密碼留給更新後搬；網頁的已在登入頁刪掉
  savePasswordCredential(email, password, name);
}

/**
 * 改密碼成功：App 有存過「這個帳號」就換成新密碼；網頁請瀏覽器更新。
 * 以前拿「最近一次存的那組」來改 —— 存了別的帳號時，會把新密碼存到別人名下
 */
export async function updateSavedPassword(email, password) {
  if (hasSecureCredentialStore()) {
    try {
      const saved = await getSavedFor(email);
      if (saved) await SecureCredentials.save({ username: saved.username, password });
    } catch (e) {
      /* 略過 */
    }
    return;
  }
  savePasswordCredential(email, password);
}

/** 清掉 App 存的帳號密碼：給了 email 只清那個帳號（舊版外掛會全部清掉） */
export async function forgetSavedLogin(email = null) {
  if (!hasSecureCredentialStore()) return;
  try {
    await SecureCredentials.remove(email ? { username: email } : {});
  } catch (e) {
    /* 略過 */
  }
}
